// src/belediye/components/tasks/IliskiliGorevlerKarti.js
// FAZ-29 — Başvuru detayındaki "İlişkili Görevler" kartı.
//
// İKİ KATMAN (KVKK — karar dok. §9):
//   info_lines : jenerik satırlar (görev başlığı YOK) — başvuruyu gören herkese
//   tasks      : başlık+durum+link — yalnız görev-görünürlüğü olana (backend süzer)
//
// Mevcut "Yönlendirme Geçmişi" kartına KARIŞTIRILMAZ (o ForwardLog'a özel).
// Modül kapalıysa uç 404 döner → kart kendini hiç göstermez. uiOn kapalıyken
// render edilmez (legacy dünyaya dal eklenmez — K5).

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";

// FAZ-51: görev ikonu TEK sözlükten (kavram değişince her yer değişsin)
import { TaskIcon } from "../icons/appIcons";

import usePermissions from "../../hooks/usePermissions";
import useFeatureEnabled from "../../hooks/useFeatureEnabled";
import useModuleEnabled from "../../hooks/useModuleEnabled";
import { fetchByApplication } from "../../services/internalTasksApi";
import { DueChip, SourceBadge, StatusChip } from "./GorevBits";
import "./gorev.css";

const trDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

export default function IliskiliGorevlerKarti({ applicationId, trackingNumber, subject }) {
  const { uiOn, can } = usePermissions();
  const moduleOn = useModuleEnabled("internal_tasks");
  // FAZ-83/7 (kullanıcı, 2026-08-01): "başvuru atma kısmı KAPALI olmasına
  // rağmen başvuru detay sayfasında ilişkili talepler kısmını, üzerindeki
  // 'Talep Oluştur' alanını görüyorum — bunları görmek istemiyorum."
  //
  // Bu kart başvuru ↔ talep KÖPRÜSÜDÜR; köprüyü açıp kapayan anahtar
  // "Başvuru sohbetleri" (mesajlasma.basvuru) bayrağıdır — sohbetteki
  // "Başvuru Ata" seçeneği de, sohbet listesindeki "Başvurular" sekmesi de
  // aynı bayrağa bağlı. Bayrak kapalıyken o iki yüzey zaten yok oluyordu;
  // başvuru sayfasındaki bu kart gözden kaçmıştı — köprünün açık kalan tek
  // ucuydu.
  //
  // ÇİFTE KAPI: modül (Kurum İçi Talepler) VE bayrak. Biri bile kapalıysa
  // kart hiç çizilmez (istek de atılmaz).
  const basvuruKopru = useFeatureEnabled("mesajlasma.basvuru");
  const navigate = useNavigate();
  const [data, setData] = useState(null); // null=yükleniyor, false=gizle

  const enabled = uiOn && moduleOn && basvuruKopru && Boolean(applicationId);

  useEffect(() => {
    if (!enabled) return;
    fetchByApplication(applicationId)
      .then(setData)
      .catch(() => setData(false)); // modül kapalı (404) / yetki yok → kart yok
  }, [enabled, applicationId]);

  if (!enabled || data === false) return null;

  const canCreate = can("tasks.create") && can("tasks.link_application");
  const hasContent =
    data && ((data.info_lines || []).length > 0 || (data.tasks || []).length > 0);

  // İçerik yok VE görev oluşturma izni de yok → kart hiç görünmesin (sade detay).
  if (data && !hasContent && !canCreate) return null;

  const createHref = () => {
    const params = new URLSearchParams();
    params.set("application", String(applicationId));
    if (trackingNumber) params.set("tracking", trackingNumber);
    if (subject) params.set("title", `Başvuru: ${String(subject).slice(0, 120)}`);
    navigate(`/yonetim/gorev-ata?${params.toString()}`);
  };

  return (
    <div className="gk-panel" data-testid="iliskili-gorevler">
      <div className="gk-page-head">
        <h4 style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
          <TaskIcon style={{ width: 18, height: 18 }} />
          İlişkili Talepler
        </h4>
        {canCreate && (
          <button type="button" className="gk-btn gk-btn--primary" onClick={createHref}>
            <PlusIcon className="gk-ic" /> Talep Oluştur
          </button>
        )}
      </div>

      {data === null && <div className="gk-empty">Yükleniyor…</div>}

      {data && (data.tasks || []).length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {data.tasks.map((t) => (
            <button
              key={t.id}
              type="button"
              className="gk-picker-item"
              style={{ borderBottom: "1px solid #f1f5f9" }}
              onClick={() => navigate(`/yonetim/gorevler/${t.id}`)}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <SourceBadge task={t} />
                <b>{t.title}</b>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <DueChip task={t} />
                <StatusChip task={t} />
              </span>
            </button>
          ))}
        </div>
      )}

      {data && (data.info_lines || []).length > 0 && (
        <div>
          {data.info_lines.map((l, i) => (
            <div key={`${l.event}-${i}`} className="gk-event">
              <span className="dot" />
              <span>
                {l.label}
                <span className="gk-source" style={{ marginLeft: 6 }}>
                  {l.source_label_display}
                </span>
                <br />
                <span style={{ fontSize: "0.74rem" }}>{trDateTime(l.created_at)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {data && !hasContent && (
        <div className="gk-empty">Bu başvuruyla ilişkili talep yok.</div>
      )}
    </div>
  );
}
